/**
 * DM-7490 referral code and Founding Partner cohort decisions.
 *
 * Kept apart from the page for the reason broadcasts.logic.ts is: pure
 * functions are what the admin-ui suite can test without rendering.
 */

import type {
  ReferralCodeItem,
  ReferralCohortCounts,
  ReferralCohortDefinitions,
} from '../api/adminOps';
import type { ChipTone } from '../components/ui/StatusChip';

/** Mirrors partner_referral_codes_code_format_check exactly. */
export const REFERRAL_CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]{2,31}$/;
export const PARTNER_NAME_MAX_LENGTH = 200;
export const REASON_MAX_LENGTH = 500;
export const DEFAULT_COHORT_DAYS = 90;
export const MAX_COHORT_RANGE_DAYS = 366;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DAY_MS = 24 * 60 * 60 * 1000;

/** The server stores codes upper-case, so the form shows what will be saved. */
export function normalizeReferralCode(value: string): string {
  return value.trim().toUpperCase();
}

export function referralCodeBlocker(value: string): string | null {
  const code = normalizeReferralCode(value);
  if (code.length === 0) return 'Code is required';
  if (!REFERRAL_CODE_PATTERN.test(code)) {
    return 'Code must be 3-32 letters, digits or hyphens, starting with a letter or digit';
  }
  return null;
}

export interface CreateReferralDraft {
  readonly code: string;
  readonly partnerName: string;
  readonly organizationId: string;
}

/**
 * Why the create form cannot be submitted, or null when it can, so the button
 * explains itself rather than sitting disabled.
 */
export function createBlocker(draft: CreateReferralDraft): string | null {
  const codeProblem = referralCodeBlocker(draft.code);
  if (codeProblem !== null) return codeProblem;
  const partner = draft.partnerName.trim();
  if (partner.length === 0) return 'Partner name is required';
  if (partner.length > PARTNER_NAME_MAX_LENGTH) {
    return `Partner name is ${partner.length - PARTNER_NAME_MAX_LENGTH} characters too long`;
  }
  const organizationId = draft.organizationId.trim();
  if (organizationId.length > 0 && !UUID_PATTERN.test(organizationId)) {
    return 'Organization id must be a UUID';
  }
  return null;
}

export function reasonBlocker(reason: string): string | null {
  const trimmed = reason.trim();
  if (trimmed.length === 0) return 'A reason is required';
  if (trimmed.length > REASON_MAX_LENGTH) {
    return `Reason is ${trimmed.length - REASON_MAX_LENGTH} characters too long`;
  }
  return null;
}

/** Only an active code can be rotated; the server refuses anything else. */
export function canRotate(code: ReferralCodeItem): boolean {
  return code.active;
}

export function rotateBlocker(
  current: ReferralCodeItem,
  newCode: string,
  reason: string
): string | null {
  if (!canRotate(current)) return 'Only an active code can be rotated';
  const codeProblem = referralCodeBlocker(newCode);
  if (codeProblem !== null) return codeProblem;
  if (normalizeReferralCode(newCode) === current.code) {
    return 'The new code must differ from the current one';
  }
  return reasonBlocker(reason);
}

export function codeStatusChip(active: boolean): { label: string; tone: ChipTone } {
  return active ? { label: 'Active', tone: 'ok' } : { label: 'Inactive', tone: 'neutral' };
}

/** The code a rotated row replaced, or null when it is unknown to the list. */
export function rotatedFromCode(
  code: ReferralCodeItem,
  codes: readonly ReferralCodeItem[]
): string | null {
  if (code.rotated_from_code_id === null) return null;
  return codes.find(candidate => candidate.id === code.rotated_from_code_id)?.code ?? null;
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** The report defaults to the last 90 days, ending today (UTC). */
export function defaultCohortRange(now: Date = new Date()): { from: string; to: string } {
  return {
    from: isoDay(new Date(now.getTime() - (DEFAULT_COHORT_DAYS - 1) * DAY_MS)),
    to: isoDay(now),
  };
}

export function cohortRangeBlocker(from: string, to: string): string | null {
  if (from.length === 0 || to.length === 0) return 'Choose both dates';
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return 'Dates must be YYYY-MM-DD';
  if (start > end) return 'The start date is after the end date';
  if ((end - start) / DAY_MS + 1 > MAX_COHORT_RANGE_DAYS) {
    return `The range is limited to ${MAX_COHORT_RANGE_DAYS} days`;
  }
  return null;
}

function percent(numerator: number, denominator: number): string {
  return `${Math.round((numerator / denominator) * 100)}%`;
}

export function activationCell(counts: ReferralCohortCounts): string {
  if (counts.verified_signups === 0) return '—';
  return `${counts.activated} (${percent(counts.activated, counts.verified_signups)})`;
}

/**
 * Retention is only a rate over signups old enough to have finished their
 * window. A cohort that is too young reads "not yet measurable", never 0%,
 * because nobody in it has had the chance to stay or leave yet.
 */
export function retentionCell(counts: ReferralCohortCounts): string {
  if (counts.retention_measurable === 0) {
    return counts.retention_not_yet_measurable > 0 ? 'Not yet measurable' : '—';
  }
  const rate = `${counts.retained} / ${counts.retention_measurable} (${percent(
    counts.retained,
    counts.retention_measurable
  )})`;
  return counts.retention_not_yet_measurable > 0
    ? `${rate}, ${counts.retention_not_yet_measurable} not yet measurable`
    : rate;
}

export function definitionsNote(definitions: ReferralCohortDefinitions): string {
  return (
    `A verified signup is an ${definitions.verified_signup}. ` +
    `Activated: a donation within ${definitions.activation_window_days} days of it. ` +
    `Retained: still has an account and signed in or donated between day ` +
    `${definitions.retention_window_start_day} and day ${definitions.retention_window_end_day}. ` +
    `Signups younger than ${definitions.retention_window_end_day} days are not yet measurable.`
  );
}
